import { useEffect, useState } from "react";
import { fetchComments, fetchDeliverables } from "@/api/tasks";
import type { Task, TaskComment } from "@/api/tasks";
import { subscribeToTaskComments } from "@/lib/task-comment-bus";
import { dedupeTaskComments } from "./task-comment-dedupe";

/** The task's comments, read from its own project. */
export function useTaskDetailComments(
  task: Task | undefined,
  localTaskComments: TaskComment[] | undefined,
  refetchSignal: number,
): [TaskComment[], (comments: TaskComment[]) => void] {
  const [comments, setComments] = useState<TaskComment[]>([]);
  const taskId = task?.id;
  const projectId = task?.project;
  const taskUpdatedAt = task?.updated_at;

  useEffect(() => {
    if (!taskId || !projectId) {
      setComments([]);
      return;
    }
    if (localTaskComments) {
      setComments(localTaskComments);
      return;
    }
    let cancelled = false;
    void fetchComments(projectId, taskId)
      .then((fetchedComments) => {
        if (cancelled) return;
        setComments((currentComments) => {
          const liveComments = currentComments.filter(
            (comment) => comment.task === taskId,
          );
          return dedupeTaskComments([...fetchedComments, ...liveComments]);
        });
      })
      .catch(() => {
        if (cancelled) return;
        setComments((currentComments) =>
          currentComments.filter((comment) => comment.task === taskId),
        );
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, taskId, taskUpdatedAt, refetchSignal, localTaskComments]);

  useEffect(() => {
    if (!taskId) return;
    return subscribeToTaskComments(({ task_id, comment }) => {
      if (task_id !== taskId) return;
      setComments((currentComments) =>
        dedupeTaskComments([...currentComments, comment]),
      );
    });
  }, [taskId]);

  return [comments, setComments];
}

/** Whether the task has deliverables, read from its own project. */
export function useTaskDetailHasDeliverables(
  task: Task | undefined,
  refetchSignal: number,
): boolean {
  const [hasDeliverables, setHasDeliverables] = useState(false);
  const taskId = task?.id;
  const projectId = task?.project;
  const hasSeededDeliverables = (task?.deliverable_frame_ids?.length ?? 0) > 0;

  useEffect(() => {
    setHasDeliverables(false);
  }, [taskId]);

  useEffect(() => {
    if (!taskId || !projectId) return;
    if (hasSeededDeliverables) {
      setHasDeliverables(true);
      return;
    }
    let cancelled = false;
    fetchDeliverables(projectId, taskId)
      .then((deliverables) => {
        if (!cancelled) setHasDeliverables(deliverables.length > 0);
      })
      .catch(() => {
        /* ignore */
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, taskId, hasSeededDeliverables, refetchSignal]);

  return hasDeliverables;
}
